import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'
import { Card, CardContent, Divider, Grid, Typography } from '@mui/material'
import { GreenText, LabelField } from 'components'
import { MichiganTitleApplicationPacketDto } from 'dtos/vehicles/titles'
import { concatAddress, formatCurrency, formatDate } from 'utils'

interface Props {
  values?: MichiganTitleApplicationPacketDto
}

export default function MichiganTitleApplicationPacketCard({ values }: Props) {
  return (
    <Card variant='outlined'>
      <CardContent>
        <Grid container spacing={1} alignItems='flex-start'>
          <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
            <Grid item xs={12} sm='auto'>
              <Typography fontWeight='bold' variant='h5'>
                Michigan Title Application Packet
              </Typography>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item sm={12} md={4} container spacing={1}>
            <Grid item xs={12}>
              <LabelField label='Owner Name' value={values?.michiganTitleOwnerName} />
            </Grid>

            <Grid item xs={12}>
              <LabelField label='Owner Address' value={values?.michiganTitleOwnerAddress1} />
            </Grid>

            <Grid item xs={12}>
              <LabelField label='Owner City' value={values?.michiganTitleOwnerCity} />
            </Grid>

            <Grid item xs={12}>
              <LabelField label='Owner County' value={values?.michiganTitleOwnerCounty} />
            </Grid>

            <Grid item xs={12}>
              <LabelField label='Owner Country' value={values?.michiganTitleOwnerCountry} />
            </Grid>

            <Grid item xs={12}>
              <LabelField label='Owner State' value={values?.michiganTitleOwnerState} />
            </Grid>

            <Grid item xs={12}>
              <LabelField label='Owner Postal Code' value={values?.michiganTitleOwnerPostalCode} />
            </Grid>

            <Grid item xs={12}>
              <LabelField label='Owner Phone' value={values?.michiganTitleOwnerPhone} />
            </Grid>

            <Grid item xs={12}>
              <LabelField label='Owner IRS #' value={values?.michiganTitleOwnerIrsNumber} />
            </Grid>

            <Grid item xs={12}>
              <LabelField label='Owner Dealer #' value={values?.michiganTitleOwnerDealerNumber} />
            </Grid>
          </Grid>

          <Grid item sm={12} md={4} container spacing={1}>
            <Grid item xs={12}>
              <LabelField label='Special Mailer Name' value={values?.michiganTitleSpecialMailerName} />
            </Grid>

            <Grid item xs={12}>
              <LabelField label='Special Mailer Address' value={values?.michiganTitleSpecialMailerAddress1} />
            </Grid>

            <Grid item xs={12}>
              <LabelField label='Special Mailer City' value={values?.michiganTitleSpecialMailerCity} />
            </Grid>

            <Grid item xs={12}>
              <LabelField label='Special Mailer State' value={values?.michiganTitleSpecialMailerState} />
            </Grid>

            <Grid item xs={12}>
              <LabelField label='Special Mailer Postal Code' value={values?.michiganTitleSpecialMailerPostalCode} />
            </Grid>
          </Grid>

          <Grid item xs={12} md={4} container spacing={1}>
            <Grid item xs={12}>
              <LabelField
                label='Registered Importer Will Not Receive Title'
                value={values?.michiganTitleRegisteredImporterWillNotReceiveTitle ? <CheckBox fontSize='small' /> : <CheckBoxOutlineBlank fontSize='small' />}
              />
            </Grid>

            <Grid item xs={12}>
              <LabelField label='Base MSRP' value={values?.michiganTitleBaseMsrp ? formatCurrency(values.michiganTitleBaseMsrp) : undefined} />
            </Grid>

            <Grid item xs={12}>
              <LabelField label='Lien Holder Company' value={values?.michiganTitleLienHolderCompanyName} />
            </Grid>

            <Grid item xs={12}>
              <LabelField
                label='Lien Holder Company Address'
                value={concatAddress(
                  values?.michiganTitleLienHolderCompanyAddress1,
                  values?.michiganTitleLienHolderCompanyAddress2,
                  values?.michiganTitleLienHolderCompanyAddressCity,
                  values?.michiganTitleLienHolderCompanyAddressStateProvince,
                  values?.michiganTitleLienHolderCompanyAddressPostalCode
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <LabelField
                label='Vehicle Purchase Agreement Date'
                value={
                  values?.michiganTitleVehiclePurchaseAgreementEffectiveDate
                    ? formatDate(values?.michiganTitleVehiclePurchaseAgreementEffectiveDate)
                    : undefined
                }
              />
            </Grid>

            <Grid item xs={12}>
              <LabelField label='Document Signer' value={values?.michiganTitleDocumentSignerPersonName} />
            </Grid>

            <Grid item xs={12}>
              <LabelField label='Police Book #' value={values?.michiganTitlePoliceBookNumber} />
            </Grid>

            <Grid item xs={12}>
              <LabelField
                label='Application Packet Ready For MI SoS Bundle'
                value={
                  values?.michiganTitleApplicationPacketReadyForMISoSBundleDateTime
                    ? `${formatDate(values.michiganTitleApplicationPacketReadyForMISoSBundleDateTime)} by ${
                        values?.michiganTitleApplicationPacketReadyForMISoSBundleByPersonName ?? '<User>'
                      }`
                    : undefined
                }
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Michigan Title Application Packet Card:**

All fields on this card are populated automatically from the saved Michigan Title Application Packet for the vehicle. These fields are read-only and cannot be edited 
on this screen. They reflect the information submitted with the Title Application and are displayed for reference purposes only.

If Title Processing State = Michigan (on Vehicle Details – Exporter tab), the Michigan Title Application Packet card is displayed.

The CAN Ownership card is populated with the name and address from the OCR’d and accepted CAN Ownership document associated with this vehicle. If the CAN 
Ownership document is from Quebec, only the Name field will be populated due to Quebec CAN Ownership documents not displaying any address information.

The Application Info Last Saved section is populated when a user on the Michigan Titles screen clicks the “Save” button for the Michigan Title Application Packet 
information.

The Application Packet Ready to Print section is populated when a user on the Michigan Titles screen clicks “Save And Mark Application Packet Ready To Print.”`}
            </GreenText>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
